import { Injectable } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { Platform } from '@ionic/angular';
import { IUser } from 'src/modules/authentication/interfaces/user.interface';
import { HttpPromiseService } from './http-promise.service';
import { UserTypeEnum } from 'src/modules/authentication/enums/user-type.enum';
import { Router } from '@angular/router';
import { UserService } from './user.service';
import { FirebaseX } from '@awesome-cordova-plugins/firebase-x/ngx';
import { environment } from 'src/environments/environment';

declare var firebase: any;

@Injectable({
    providedIn: 'root'
})
export class PushNotificationService {
    private firebaseWebInitialized = false;

    constructor(
        private localStorageService: LocalStorageService,
        private platform: Platform,
        private httpPromiseService: HttpPromiseService,
        private router: Router,
        private userService: UserService,
        private firebaseX: FirebaseX
    ) { }

    initPushNotification(currentUser: IUser) {
        if (!currentUser) { return; }
        if (this.isMobileDevice()) {
            this.initFirebase(currentUser);
        } else {
            if (!this.firebaseWebInitialized && window.location.href.indexOf('localhost') < 0) {
                this.loadFirebaseScripts(() => {
                    this.initFirebaseWeb(currentUser);
                });
            }
        }
    }

    isMobileDevice() {
        return this.platform.is('cordova');
    }

    // ======================== NATIVE (Cordova) ========================

    async initFirebase(currentUser: IUser) {
        try {
            const hasPermission = await this.firebaseX.hasPermission();
            if (!hasPermission) {
                await this.firebaseX.grantPermission();
            }
            await this.registerPush(currentUser);
        } catch (err) {
            console.error('Error during Firebase permission or init', err);
        }
    }

    private async registerPush(currentUser: IUser) {
        try {
            const pushToken = await this.firebaseX.getToken();
            console.log('PUSH TOKEN (native)', pushToken);
            this.localStorageService.setSingleKey('deviceToken', pushToken);
            await this.httpPromiseService.httpPostRequest(
                `/api/users/token?provider=firebase`, { token: pushToken }
            );
        } catch (err) {
            console.error('FirebaseX getToken error:', err);
        }

        this.firebaseX.onMessageReceived().subscribe(rawData => {
            let data: any;
            try {
                data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
            } catch (err) {
                console.error('Failed to parse notification data', err);
                return;
            }

            if (data.tap === 'background') {
                const notificationId = data.notificationId || data.userdata?.notificationId;
                if (notificationId) {
                    this.navigateToSingleNotificationView(notificationId);
                }
            }
        });

        this.firebaseX.onTokenRefresh().subscribe(async (newToken: string) => {
            this.localStorageService.setSingleKey('deviceToken', newToken);
            try {
                await this.httpPromiseService.httpPostRequest(
                    `/api/users/token?provider=firebase`, { token: newToken }
                );
            } catch (err) {
                console.error('Error updating refreshed token', err);
            }
        });
    }

    // ======================== WEB (Firebase Messaging via CDN) ========================

    private loadFirebaseScripts(callback: () => void) {
        const head = document.getElementsByTagName('head')[0];

        const appScript = document.createElement('script');
        appScript.type = 'text/javascript';
        appScript.src = 'https://www.gstatic.com/firebasejs/9.23.0/firebase-app-compat.js';
        appScript.onload = () => {
            const messagingScript = document.createElement('script');
            messagingScript.type = 'text/javascript';
            messagingScript.src = 'https://www.gstatic.com/firebasejs/9.23.0/firebase-messaging-compat.js';
            messagingScript.onload = callback;
            head.appendChild(messagingScript);
        };
        head.appendChild(appScript);
    }

    async initFirebaseWeb(currentUser: IUser) {
        try {
            if (typeof firebase === 'undefined') {
                console.log('Firebase SDK not loaded');
                return;
            }

            if (!firebase.messaging.isSupported()) {
                console.log('Firebase Messaging not supported in this browser');
                return;
            }

            firebase.initializeApp(environment.firebase);
            const messaging = firebase.messaging();
            this.firebaseWebInitialized = true;

            // Register service worker
            const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                console.log('Notification permission denied');
                return;
            }

            const token = await messaging.getToken({
                serviceWorkerRegistration: swRegistration
            });

            if (token) {
                console.log('FCM web token:', token);
                this.localStorageService.setSingleKey('deviceToken', token);
                await this.httpPromiseService.httpPostRequest(
                    `/api/users/token?provider=firebase`, { token }
                );
            }

            // Handle foreground messages
            messaging.onMessage((payload) => {
                console.log('Foreground message received:', payload);
            });

        } catch (err) {
            console.error('Error initializing Firebase Web push:', err);
        }
    }

    // ======================== SHARED ========================

    async navigateToSingleNotificationView(notificationId: string) {
        const currentUser: IUser = await this.localStorageService.getLoggedInUserInfo();
        if (currentUser) {
            if (currentUser.type === UserTypeEnum.STAFFER) {
                this.router.navigateByUrl(`talent-dashboard/notifications?id=${notificationId}`);
            } else if (currentUser.type === UserTypeEnum.EMPLOYER) {
                this.router.navigateByUrl(`company-dashboard/notifications?id=${notificationId}`);
            }
        }
    }

    async removePushNotificationToken() {
        const token = await this.localStorageService.getSingleKey('deviceToken');
        if (!token) { return; }

        try {
            await this.httpPromiseService.httpPostRequest(
                `/api/users/rtoken`, { token }
            );
        } catch (error) {
            console.log('err updating token to server', error);
        }
    }
}
